import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FlaskConical, Play, Pause, Square, Loader2, Info, AlertTriangle, ChevronLeft, Pencil, Check, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axiosInstance from '../utils/axiosInstance';
import ShippyLoader from '../components/ShippyLoader';

/**
 * Shipping experiments (A/B shipping discount at checkout).
 * Every number comes from the backend (shippy/experiment_stats.py); this page only renders.
 */

// Two-series palette, validated (dataviz validator, light + dark): control = blue, treatment = orange.
const ARM_CLASS = {
  control: 'text-[#2a78d6] dark:text-[#3987e5]',
  treatment: 'text-[#eb6834] dark:text-[#d95926]',
};
const ARM_BG = {
  control: 'bg-[#2a78d6] dark:bg-[#3987e5]',
  treatment: 'bg-[#eb6834] dark:bg-[#d95926]',
};
const ARMS = ['control', 'treatment'];

const pct = (x, d = 1) => (x === null || x === undefined ? '–' : `${(x * 100).toFixed(d)}%`);
const pts = (x, d = 1) => (x === null || x === undefined ? '–' : `${x >= 0 ? '+' : ''}${(x * 100).toFixed(d)} pts`);
const eur = (x, d = 2) => (x === null || x === undefined ? '–' : `€${Number(x).toLocaleString('en-GB', { minimumFractionDigits: d, maximumFractionDigits: d })}`);
const signedEur = (x) => (x === null || x === undefined ? '–' : `${x >= 0 ? '+' : '−'}${eur(Math.abs(x))}`);
const num = (x, d = 1) => (x === null || x === undefined ? '–' : Number(x).toFixed(d));
const dts = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const dstr = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const STATUS_STYLE = {
  draft: 'bg-muted text-muted-foreground',
  running: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  paused: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200',
  ended: 'bg-slate-200 text-slate-700 dark:bg-slate-700/60 dark:text-slate-200',
};

function StatusBadge({ status }) {
  return <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${STATUS_STYLE[status] || STATUS_STYLE.draft}`}>{status}</span>;
}

/** Checkout options live in a country whose price relates to the default rate
 * (only the default rate is discounted): one amber chip per country. */
function OverlapChips({ overlaps, className = '' }) {
  const entries = Object.entries(overlaps || {});
  if (!entries.length) return null;
  return (
    <span className={`inline-flex flex-wrap gap-1 ${className}`}>
      {entries.map(([iso, opts]) => (
        <span key={iso}
          title={`${iso} also offers ${opts.map((o) => o.label).join(', ')} at checkout. Only the default rate is discounted; these keep their normal price (${opts.map((o) => o.config).join(', ')}).`}
          className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
          <AlertTriangle className="h-2.5 w-2.5" /> {iso}: {opts.map((o) => o.label).join(', ')}
        </span>
      ))}
    </span>
  );
}

function ArmDot({ arm }) {
  return <span className={`inline-block h-2.5 w-2.5 rounded-full ${ARM_BG[arm]}`} aria-hidden="true" />;
}

function Bar({ value, max, className = 'bg-primary', label }) {
  const w = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div className="h-2 w-full rounded-full bg-muted overflow-hidden" role="meter" aria-valuenow={value} aria-valuemax={max} aria-label={label}>
      <div className={`h-full rounded-full ${className}`} style={{ width: `${w}%` }} />
    </div>
  );
}

function Prob({ p }) {
  if (p === null || p === undefined) return <span className="text-muted-foreground">–</span>;
  const strong = p >= 0.9 || p <= 0.1;
  return <span className={`tabular-nums ${strong ? 'font-semibold' : ''}`}>{pct(p, 0)}</span>;
}

/** Cumulative line chart: one measure, control vs treatment, crosshair tooltip. */
function CumulativeChart({ series, field, title, format }) {
  const [hover, setHover] = useState(null);
  const w = 560, h = 180, padL = 48, padR = 72, padT = 12, padB = 24;
  const pts_ = series.map((p, i) => ({ i, date: p.date, c: p.control?.[field], t: p.treatment?.[field], nc: p.control?.carts, nt: p.treatment?.carts }));
  const vals = pts_.flatMap((p) => [p.c, p.t]).filter((v) => v !== null && v !== undefined);
  if (pts_.length < 2 || vals.length === 0) {
    return (
      <div className="rounded-lg border border-border p-4">
        <div className="text-sm font-medium">{title}</div>
        <div className="mt-6 mb-6 text-xs text-muted-foreground">The line builds up once carts have settled (3 days after their first quote).</div>
      </div>
    );
  }
  const hi = Math.max(...vals) * 1.1 || 1;
  const x = (i) => padL + (i / (pts_.length - 1)) * (w - padL - padR);
  const y = (v) => padT + (1 - v / hi) * (h - padT - padB);
  const path = (key) => pts_.map((p, j) => (p[key] === null || p[key] === undefined ? null : `${j && pts_[j - 1][key] !== null && pts_[j - 1][key] !== undefined ? 'L' : 'M'}${x(p.i).toFixed(1)},${y(p[key]).toFixed(1)}`)).filter(Boolean).join(' ');
  const last = pts_[pts_.length - 1];
  const ticks = [0, hi / 2, hi];
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * w;
    const i = Math.round(((px - padL) / (w - padL - padR)) * (pts_.length - 1));
    setHover(Math.max(0, Math.min(pts_.length - 1, i)));
  };
  const hp = hover !== null ? pts_[hover] : null;
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium">{title}</div>
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          {ARMS.map((a) => <span key={a} className="inline-flex items-center gap-1"><ArmDot arm={a} /> {a}</span>)}
        </div>
      </div>
      <div className="relative mt-2">
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto" onMouseMove={onMove} onMouseLeave={() => setHover(null)} role="img" aria-label={title}>
          {ticks.map((tv, k) => (
            <g key={k}>
              <line x1={padL} x2={w - padR} y1={y(tv)} y2={y(tv)} stroke="currentColor" className="text-border" strokeWidth="1" />
              <text x={padL - 6} y={y(tv) + 3} textAnchor="end" className="fill-muted-foreground" fontSize="10">{format(tv)}</text>
            </g>
          ))}
          <text x={padL} y={h - 6} className="fill-muted-foreground" fontSize="10">{dstr(pts_[0].date)}</text>
          <text x={w - padR} y={h - 6} textAnchor="end" className="fill-muted-foreground" fontSize="10">{dstr(last.date)}</text>
          {ARMS.map((a) => {
            const key = a === 'control' ? 'c' : 't';
            return (
              <g key={a} className={ARM_CLASS[a]}>
                <path d={path(key)} fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                {last[key] !== null && last[key] !== undefined && (
                  <>
                    <circle cx={x(last.i)} cy={y(last[key])} r="4" fill="currentColor" stroke="hsl(var(--card))" strokeWidth="2" />
                    <text x={x(last.i) + 8} y={y(last[key]) + 3} fontSize="10" className="fill-foreground">{format(last[key])}</text>
                  </>
                )}
              </g>
            );
          })}
          {hp && (
            <line x1={x(hp.i)} x2={x(hp.i)} y1={padT} y2={h - padB} stroke="currentColor" className="text-muted-foreground" strokeDasharray="3 3" />
          )}
        </svg>
        {hp && (
          <div className="pointer-events-none absolute top-0 rounded-md border border-border bg-popover px-2 py-1.5 text-[11px] shadow-sm"
            style={{ left: `${Math.min(70, (x(hp.i) / w) * 100)}%` }}>
            <div className="font-medium">{dstr(hp.date)}</div>
            {ARMS.map((a) => {
              const v = a === 'control' ? hp.c : hp.t;
              const n = a === 'control' ? hp.nc : hp.nt;
              return <div key={a} className="flex items-center gap-1.5"><ArmDot arm={a} /> {a}: <b className="tabular-nums">{v === null || v === undefined ? '–' : format(v)}</b> <span className="text-muted-foreground">({n} carts)</span></div>;
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function SettlingNote({ arms, settlingDays, discountLabel = 'Discount given' }) {
  // Why "Discount given" can show money while Orders still reads 0: the money
  // counts the moment an order is placed, the groups only count a cart once it
  // is settlingDays old (TW+HK day 1, 2026-09-30).
  const list = Object.values(arms || {});
  const settling = list.reduce((n, a) => n + (a?.settling_carts || 0), 0);
  const settlingOrders = list.reduce((n, a) => n + (a?.settling_orders || 0), 0);
  if (!settling) return null;
  return (
    <div className="text-[11px] italic text-muted-foreground">
      ({settling} cart{settling === 1 ? '' : 's'} still settling: a cart only enters the groups {settlingDays || 3} days after its first quote, since customers often order a day or two later.
      {settlingOrders > 0
        ? ` ${settlingOrders} of them already ordered: those orders are in ${discountLabel} at once and appear as "+${settlingOrders}" above, but not yet in the rates.`
        : ` Orders they place count in ${discountLabel} at once, and in the rates once settled.`})
    </div>
  );
}

function ScopePanel({ block, settlingDays, franco = false }) {
  const c = block.arms.control;
  const t = block.arms.treatment;
  const cmp = block.compare || {};
  const conv = cmp.conversion;
  const prof = cmp.profit;
  const ss = cmp.sample_size || {};
  const reasons = (a) => {
    const list = a.not_eligible_reasons || [];
    if (!list.length) return null;
    return <span className="text-muted-foreground text-[11px]"> ({list.slice(0, 3).map((r) => `${r.label}: ${r.quotes}`).join(', ')}{list.length > 3 ? ', …' : ''})</span>;
  };
  const rows = [
    ['Quotes logged (all)', (a) => a.quotes ?? 0, 'Every checkout quote logged for this arm, eligible or not (repeat quotes of the same cart count each time)'],
    ['Not eligible', (a) => <>{a.not_eligible_quotes ?? 0}{reasons(a)}</>, 'Quotes that could not get a discount, with the most frequent reasons'],
    ['Eligible carts (settled)', (a) => a.carts, 'Distinct address + cart pairs quoted eligible, first seen more than ' + settlingDays + ' days ago'],
    ['Still settling', (a) => a.settling_carts, 'First quoted in the last ' + settlingDays + ' days: the customer may still order, so left out of the rates'],
    ['Orders', (a) => <>{a.orders ?? 0}{a.settling_orders ? <span className="text-muted-foreground text-[11px]"> +{a.settling_orders} settling</span> : null}</>, 'Orders from settled carts; "+N settling" are orders already placed by carts still inside the ' + settlingDays + '-day window (they count in Discount given, not yet in the rates)'],
    ['Conversion', (a) => <>{pct(a.conversion)} <span className="text-muted-foreground text-[11px]">({pct(a.conversion_lo)} to {pct(a.conversion_hi)})</span></>, '95% Wilson interval'],
    ['Profit per eligible cart', (a) => <>{eur(a.profit_per_cart)} <span className="text-muted-foreground text-[11px]">({eur(a.profit_lo)} to {eur(a.profit_hi)})</span></>, 'Gross margin of attributed orders / eligible carts, 95% normal interval'],
    ['Avg margin per order', (a) => eur(a.avg_margin_per_order)],
    ['Total profit', (a) => <span className="font-semibold">{eur(a.profit_total)}</span>, 'Sum of the profit of every order in this group, after the discount or free shipping given (the customer paid less shipping, we paid the full carrier cost)'],
    ['Average eligible cart', (a) => eur(a.avg_cart_value), 'Average products value of the carts that entered the test (ordered or not), ex VAT'],
    ['Average order value (AOV)', (a) => <>{eur(a.basket_per_order)}{a.basket_se ? <span className="text-muted-foreground text-[11px]"> (±{eur(1.96 * a.basket_se)})</span> : null}</>, 'Average products value of the orders placed (after discounts, ex VAT, shipping not included)'],
    ...(franco ? [
      ['Revenue per eligible cart', (a) => <>{eur(a.revenue_per_cart)}{a.revenue_se ? <span className="text-muted-foreground text-[11px]"> (±{eur(1.96 * a.revenue_se)})</span> : null}</>, 'Order value of attributed orders / eligible carts (0 for a cart that did not order), 95% normal interval'],
    ] : []),
    [franco ? 'Free shipping given' : 'Discount spent', (a) => eur(a.discount_spent), franco ? 'Shipping the treatment customers would have paid without the test (ex VAT), on attributed orders' : ''],
  ];
  const rev = cmp.revenue;
  const basket = cmp.basket;
  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs">
            <tr>
              <th className="px-3 py-2 font-semibold">Per arm</th>
              {ARMS.map((a) => <th key={a} className="px-3 py-2 font-semibold"><span className="inline-flex items-center gap-1.5"><ArmDot arm={a} /> {a}</span></th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, fn, hint]) => (
              <tr key={label} className="border-t border-border/60">
                <td className="px-3 py-1.5 text-muted-foreground" title={hint || ''}>{label}</td>
                <td className="px-3 py-1.5 tabular-nums">{fn(c)}</td>
                <td className="px-3 py-1.5 tabular-nums">{fn(t)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <NormalAov n={block.normal_aov} className="-mt-2" />
      <p className="-mt-2 text-[11px] text-muted-foreground">
        Only eligible carts count. A cart counts once {settlingDays} days have passed since its first quote (customers often order a day or two later); until then it is still settling.
      </p>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Conversion, treatment minus control" value={conv ? pts(conv.diff) : '–'}
          sub={conv ? `95% CI ${pts(conv.lo)} to ${pts(conv.hi)}` : 'needs carts in both arms'}
          extra={<>P(treatment converts better) <Prob p={conv?.p_better} /></>} />
        <Stat label="Profit per eligible cart, difference" value={prof ? signedEur(prof.diff) : '–'}
          sub={prof ? `95% CI ${signedEur(prof.lo)} to ${signedEur(prof.hi)}` : 'needs 2+ carts per arm'}
          extra={<>P(treatment earns more per cart) <Prob p={prof?.p_better} /></>} />
        <Stat label="Extra orders vs control" value={cmp.extra_orders === null || cmp.extra_orders === undefined ? '–' : `${cmp.extra_orders >= 0 ? '+' : ''}${num(cmp.extra_orders)}`}
          sub="Treatment orders minus control's rate at treatment's size" />
        <Stat label="Cost per extra order" value={eur(cmp.cost_per_extra_order)}
          sub={cmp.cost_per_extra_order ? (franco ? 'Free shipping given / extra orders' : 'Treatment discount spent / extra orders') : 'no extra orders yet'} />
        {franco && (
          <>
            <Stat label="Revenue per eligible cart, difference" value={rev ? signedEur(rev.diff) : '–'}
              sub={rev ? `95% CI ${signedEur(rev.lo)} to ${signedEur(rev.hi)}` : 'needs 2+ carts per arm'}
              extra={<>P(treatment more revenue per cart) <Prob p={rev?.p_better} /></>} />
          </>
        )}
        <Stat label="Net result vs normal prices" value={cmp.extra_profit === null || cmp.extra_profit === undefined ? '–' : signedEur(cmp.extra_profit)}
          sub={franco ? 'Test carts x (test profit per cart - control profit per cart), free shipping given already deducted' : 'Test carts x (test profit per cart - control profit per cart), discount given already deducted'} />
        <Stat label="AOV, difference" value={basket ? signedEur(basket.diff) : '–'}
          sub={basket ? `95% CI ${signedEur(basket.lo)} to ${signedEur(basket.hi)}` : 'needs 2+ orders per arm'}
          extra={<>P(treatment orders larger) <Prob p={basket?.p_better} /></>} />
      </div>

      <div className="rounded-lg border border-border p-4 space-y-3">
        <div className="text-sm font-medium">Sample size (one-sided 90%, power 80%, from the observed data)</div>
        <SampleRow label="Conversion read" have={ss.carts_per_arm_so_far} need={ss.needed_conversion}
          note={`detects ${pts(ss.conversion_delta)} (${ss.conversion_delta_source || '–'})`} />
        <SampleRow label="Profit read" have={ss.carts_per_arm_so_far} need={ss.needed_profit}
          note={ss.profit_delta ? `detects ${eur(ss.profit_delta)} per cart (10% of control)` : 'needs control profit first'} />
        {ss.break_even_lift !== null && ss.break_even_lift !== undefined && (
          <div className="text-[11px] text-muted-foreground">Break-even lift: treatment must convert {pts(ss.break_even_lift)} better than control to pay for the {franco ? 'free shipping' : 'discount'} on orders it would have got anyway.</div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub, extra }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
      {extra && <div className="text-xs mt-1.5">{extra}</div>}
    </div>
  );
}

function SampleRow({ label, have, need, note }) {
  return (
    <div className="grid grid-cols-[120px_1fr_auto] items-center gap-3 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <Bar value={have || 0} max={need || 0} label={label} />
      <span className="tabular-nums whitespace-nowrap">{have ?? 0} / {need ?? '–'} carts per arm <span className="text-muted-foreground">· {note}</span></span>
    </div>
  );
}

function ConfigEditor({ exp, onSaved, onCancel }) {
  const [rows, setRows] = useState(() => Object.entries(exp.countries || {}).map(([iso, r]) => ({ iso, ...r })));
  const [budget, setBudget] = useState(exp.weekly_budget_eur);
  const [weeks, setWeeks] = useState(exp.planned_weeks);
  const [split, setSplit] = useState(exp.split);
  const [err, setErr] = useState('');
  const [overlapErr, setOverlapErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const draft = exp.status === 'draft';
  const franco = exp.rule_type === 'franco_threshold';
  const set = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const save = async (allowOverlap = false) => {
    setBusy(true); setErr(''); setOverlapErr(null);
    try {
      const countries = Object.fromEntries(rows.filter((r) => r.iso).map((r) => [r.iso.toUpperCase(), franco
        ? { from: parseFloat(r.from), to: parseFloat(r.to), floor: parseFloat(r.floor) }
        : {
          floor: parseFloat(r.floor), cap: parseFloat(r.cap), min_share: parseFloat(r.min_share),
          max_discount_eur: parseFloat(r.max_discount_eur ?? 75) }]));
      const body = { countries, weekly_budget_eur: parseFloat(budget), planned_weeks: parseInt(weeks, 10) };
      if (draft) body.split = parseFloat(split);
      if (allowOverlap) body.allow_overlap = true;
      await axiosInstance.patch(`api/experiments/${exp.id}/`, body);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.error || e.message);
      if (e?.response?.data?.overlaps) setOverlapErr(e.response.data.overlaps);
    } finally { setBusy(false); }
  };
  const inp = 'w-20 rounded border border-border bg-background px-2 py-1 text-xs tabular-nums';
  return (
    <div className="rounded-lg border border-border p-4 space-y-3 text-xs">
      <div className="text-sm font-medium">Edit configuration <span className="text-muted-foreground font-normal">(fractions: 0.26 = 26%)</span></div>
      {franco ? (
        <table className="text-xs">
          <thead><tr className="text-left text-muted-foreground"><th className="pr-3">Country</th><th className="pr-3" title="The live B2C franco threshold (EUR, VAT incl). The test does nothing where the live threshold differs.">From €</th><th className="pr-3" title="The test group's threshold (EUR, VAT incl)">To €</th><th className="pr-3" title="Margin if shipping were free, share of the cart ex VAT">Floor</th><th /></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="pr-3 py-1"><input className={inp} value={r.iso} disabled={!draft} onChange={(e) => set(i, 'iso', e.target.value)} /></td>
                <td className="pr-3"><input className={inp} value={r.from ?? ''} onChange={(e) => set(i, 'from', e.target.value)} /></td>
                <td className="pr-3"><input className={inp} value={r.to ?? ''} onChange={(e) => set(i, 'to', e.target.value)} /></td>
                <td className="pr-3"><input className={inp} value={r.floor} onChange={(e) => set(i, 'floor', e.target.value)} /></td>
                <td>{draft && <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} aria-label="Remove country"><X className="h-3.5 w-3.5" /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
      <table className="text-xs">
        <thead><tr className="text-left text-muted-foreground"><th className="pr-3">Country</th><th className="pr-3">Floor</th><th className="pr-3">Cap</th><th className="pr-3">Min share</th><th className="pr-3">Max EUR / order</th><th /></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="pr-3 py-1"><input className={inp} value={r.iso} disabled={!draft} onChange={(e) => set(i, 'iso', e.target.value)} /></td>
              <td className="pr-3"><input className={inp} value={r.floor} onChange={(e) => set(i, 'floor', e.target.value)} /></td>
              <td className="pr-3"><input className={inp} value={r.cap} onChange={(e) => set(i, 'cap', e.target.value)} /></td>
              <td className="pr-3"><input className={inp} value={r.min_share} onChange={(e) => set(i, 'min_share', e.target.value)} /></td>
              <td className="pr-3"><input className={inp} value={r.max_discount_eur ?? 75} onChange={(e) => set(i, 'max_discount_eur', e.target.value)} /></td>
              <td>{draft && <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} aria-label="Remove country"><X className="h-3.5 w-3.5" /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      )}
      {draft && <Button size="sm" variant="outline" onClick={() => setRows((rs) => [...rs, franco ? { iso: '', from: 300, to: 225, floor: 0.26 } : { iso: '', floor: 0.26, cap: 0.1, min_share: 0.15, max_discount_eur: 75 }])}>Add country</Button>}
      <div className="flex flex-wrap gap-4">
        <label className="inline-flex items-center gap-2">Weekly budget € <input className={inp} value={budget} onChange={(e) => setBudget(e.target.value)} /></label>
        <label className="inline-flex items-center gap-2">Planned weeks <input className={inp} value={weeks} onChange={(e) => setWeeks(e.target.value)} /></label>
        <label className="inline-flex items-center gap-2" title={draft ? '' : 'Only in draft: changing the split mid-run moves addresses between arms'}>Treatment share <input className={inp} value={split} disabled={!draft} onChange={(e) => setSplit(e.target.value)} /></label>
      </div>
      {!draft && <div className="text-muted-foreground">Paused: {franco ? 'thresholds, floors' : 'floors, caps'}, budget and duration can change; countries and the split only in draft.</div>}
      {err && <div className="text-red-700 dark:text-red-300">{err}</div>}
      {overlapErr && (
        <div className="flex flex-wrap items-center gap-2">
          <OverlapChips overlaps={overlapErr} />
          <Button size="sm" variant="outline" disabled={busy} onClick={() => save(true)}>Add anyway (allow_overlap, logged)</Button>
        </div>
      )}
      <div className="flex gap-2">
        <Button size="sm" disabled={busy} onClick={() => save()}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}<span className="ml-1">Save</span></Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

function Detail({ id, onBack }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [flash, setFlash] = useState('');
  const [confirmStart, setConfirmStart] = useState(false);
  const [allowOverlap, setAllowOverlap] = useState(false);
  const [editing, setEditing] = useState(false);
  const [scope, setScope] = useState(null);

  const [loadedAt, setLoadedAt] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await axiosInstance.get(`api/experiments/${id}/`);
      setData(r.data);
      setError('');
      setLoadedAt(new Date());
    } catch (e) {
      setError(`Could not load the experiment (${e?.response?.status || e.message}).`);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  // Live view: refresh every 60 s while the tab is visible and nobody is editing.
  useEffect(() => {
    if (editing) return undefined;
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 60000);
    return () => clearInterval(t);
  }, [load, editing]);

  const act = async (action) => {
    if (action === 'end' && !window.confirm('End this experiment? Discounts stop within 60 seconds and it cannot be restarted.')) return;
    setBusy(action);
    try {
      await axiosInstance.post(`api/experiments/${id}/action/`, { action, confirm: action === 'start', ...(action === 'start' && allowOverlap ? { allow_overlap: true } : {}) });
      setFlash(action === 'start' ? 'Started. Checkout workers pick it up within 60 seconds.' : action === 'pause' ? 'Paused. Discounts stop within 60 seconds; attribution of recent quotes continues.' : 'Ended.');
      setConfirmStart(false);
      await load();
    } catch (e) {
      setFlash(`${action} failed: ${e?.response?.data?.error || e.message}`);
    } finally { setBusy(''); }
  };

  const summary = data?.summary;
  const scopes = summary?.scopes || [];
  const activeScope = scope && scopes.includes(scope) ? scope : (scopes.includes('ALL') ? 'ALL' : scopes[0]);

  if (error) return <div className="rounded border border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200 px-3 py-2 text-sm">{error}</div>;
  if (!data) return <ShippyLoader message="Reading the experiment" />;
  const exp = data.experiment;
  const budget = summary.budget;
  const countries = Object.entries(exp.countries || {});
  const franco = exp.rule_type === 'franco_threshold';
  const warnings = exp.threshold_warnings || [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <button type="button" onClick={onBack} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><ChevronLeft className="h-3.5 w-3.5" /> All experiments</button>
          <h1 className="text-xl font-semibold flex items-center gap-2 mt-1"><FlaskConical className="h-5 w-5" /> {exp.name} <StatusBadge status={exp.status} /></h1>
          <div className="text-xs text-muted-foreground mt-0.5">
            {exp.slug} · {franco ? 'franco threshold test' : 'shipping discount'} · {countries.map(([c]) => c).join(', ')} · {Math.round(exp.split * 100)}% of addresses in treatment
            {exp.started_at && <> · started {dstr(exp.started_at)}</>}{exp.ended_at && <> · ended {dstr(exp.ended_at)}</>}
          </div>
          <OverlapChips overlaps={exp.overlaps} className="mt-1.5" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(exp.status === 'draft' || exp.status === 'paused') && (
            <>
              <Button size="sm" variant="outline" onClick={() => setEditing((v) => !v)}><Pencil className="h-3.5 w-3.5" /><span className="ml-1">Edit</span></Button>
              <Button size="sm" disabled={!!busy} onClick={() => setConfirmStart(true)}><Play className="h-3.5 w-3.5" /><span className="ml-1">{exp.status === 'paused' ? 'Resume' : 'Start'}</span></Button>
            </>
          )}
          {exp.status === 'running' && (
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => act('pause')}>{busy === 'pause' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Pause className="h-3.5 w-3.5" />}<span className="ml-1">Pause</span></Button>
          )}
          {(exp.status === 'running' || exp.status === 'paused') && (
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => act('end')}><Square className="h-3.5 w-3.5" /><span className="ml-1">End</span></Button>
          )}
        </div>
      </div>

      {confirmStart && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100 p-4 text-sm space-y-2">
          <div className="font-medium flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> This changes live checkout prices</div>
          <p>
            {franco ? (
              <>From the next quote, {Math.round(exp.split * 100)}% of addresses in {countries.map(([c]) => c).join(', ')} get FREE shipping from a lower
              threshold ({countries.map(([c, r]) => `${c} €${r.to} instead of €${r.from}`).join(', ')}) on eligible carts (margin if shipping were free at least
              {' '}{countries.map(([c, r]) => `${c} ${pct(r.floor, 0)}`).join(', ')}; islands and territories never), up to {eur(exp.weekly_budget_eur, 0)} of free shipping on orders per week.</>
            ) : (
              <>From the next quote, {Math.round(exp.split * 100)}% of addresses in {countries.map(([c]) => c).join(' and ')} get a lower default shipping rate on
              eligible carts (margin floor {countries.map(([c, r]) => `${c} ${pct(r.floor, 0)}`).join(', ')}), up to {eur(exp.weekly_budget_eur, 0)} of discount on orders per week.</>
            )}
            {!data.kill_switch_on && ' The global switch SHIPPING_EXPERIMENTS_ENABLED is OFF in code, so nothing will actually change at checkout until it is deployed on.'}
          </p>
          {Object.keys(exp.overlaps || {}).length > 0 && (
            <div className="space-y-1.5 rounded-md border border-amber-400 bg-amber-100/60 p-2.5 dark:border-amber-700 dark:bg-amber-900/30">
              <OverlapChips overlaps={exp.overlaps} />
              <p className="text-xs">
                Only the default rate is discounted. These options keep their normal price: a pickup point can end up dearer than
                discounted home delivery, Split Shipment stays anchored on the undiscounted rate, alternative-carrier premiums widen.
                Starting needs an explicit override, logged in the notes.
              </p>
              <label className="inline-flex items-center gap-1.5 text-xs font-medium">
                <input type="checkbox" checked={allowOverlap} onChange={(e) => setAllowOverlap(e.target.checked)} className="h-3.5 w-3.5" />
                I have decided how to treat them: start anyway (allow_overlap)
              </label>
            </div>
          )}
          <div className="flex gap-2">
            <Button size="sm" disabled={!!busy || (Object.keys(exp.overlaps || {}).length > 0 && !allowOverlap)} onClick={() => act('start')}>{busy === 'start' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}<span className="ml-1">Yes, {exp.status === 'paused' ? 'resume' : 'start'} it</span></Button>
            <Button size="sm" variant="outline" onClick={() => setConfirmStart(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {!data.kill_switch_on && (
        <div className="rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm flex items-center gap-2"><Info className="h-4 w-4 shrink-0" />
          Global switch <code className="text-xs">PackingConfig.SHIPPING_EXPERIMENTS_ENABLED</code> is off: no quote is discounted or logged, whatever the status.
        </div>
      )}
      {warnings.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100 px-3 py-2 text-sm space-y-1">
          <div className="font-medium flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> Live franco threshold differs from the experiment</div>
          {warnings.map((w) => <div key={w.country} className="text-xs">{w.message}</div>)}
        </div>
      )}
      {flash && <div className="rounded border border-border bg-muted/50 px-3 py-2 text-sm flex items-center gap-2"><Info className="h-4 w-4 shrink-0" /> {flash}</div>}
      {editing && <ConfigEditor exp={exp} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); setFlash('Saved.'); load(); }} />}

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-lg border border-border p-4 space-y-2">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Progress</div>
          <div className="text-lg font-semibold tabular-nums">{summary.day_x ? `Day ${summary.day_x} of ${summary.planned_days}` : 'Not started'}</div>
          {loadedAt && <div className="text-xs text-muted-foreground tabular-nums">Live, updates every minute (last {loadedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})</div>}
          <Bar value={summary.day_x || 0} max={summary.planned_days} label="days" />
          <div className="text-[11px] text-muted-foreground">{summary.eligible_carts ?? 0} eligible cart{summary.eligible_carts === 1 ? '' : 's'} ({summary.settling_carts ?? 0} settling) · {summary.orders_total} attributed order{summary.orders_total === 1 ? '' : 's'} · {summary.funnel.quotes} quotes logged</div>
        </div>
        <div className="rounded-lg border border-border p-4 space-y-2">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Budget this week ({budget.week})</div>
          <div className="text-lg font-semibold tabular-nums">{eur(budget.spent)} <span className="text-sm font-normal text-muted-foreground">of {eur(budget.budget, 0)}</span></div>
          <Bar value={budget.spent} max={budget.budget} label="weekly budget" className={budget.exhausted ? 'bg-red-600 dark:bg-red-500' : 'bg-primary'} />
          <div className="text-[11px] text-muted-foreground">{budget.exhausted ? 'Spent: treatment quotes are on normal prices until Monday.' : (franco ? 'Free shipping given (what they would have paid, ex VAT) on orders placed this ISO week, counted at once.' : 'Discount given on orders placed this ISO week, counted at once (the rates below wait for carts to settle).')}</div>
        </div>
        <div className="rounded-lg border border-border p-4 space-y-2">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Rule per country</div>
          {franco ? (
            <>
              <table className="w-full text-xs">
                <thead><tr className="text-left text-muted-foreground"><th className="font-normal">Country</th><th className="font-normal">Threshold</th><th className="font-normal">Floor</th></tr></thead>
                <tbody>
                  {countries.map(([c, r]) => (
                    <tr key={c} className="tabular-nums"><td className="font-medium">{c}</td><td>{eur(r.from, 0)} → {eur(r.to, 0)}</td><td>{r.floor > 0 ? pct(r.floor, 0) : 'none (plain test)'}</td></tr>
                  ))}
                </tbody>
              </table>
              <div className="text-[11px] text-muted-foreground">test group: free shipping for carts between the two thresholds (VAT incl){countries.some(([, r]) => r.floor > 0) ? ', when the margin with free shipping stays at or above the floor' : ', the whole band, no margin floor (plain test)'}; islands and territories excluded</div>
            </>
          ) : (
            <>
              <table className="w-full text-xs">
                <thead><tr className="text-left text-muted-foreground"><th className="font-normal">Country</th><th className="font-normal">Floor</th><th className="font-normal">Cap</th><th className="font-normal">Min share</th></tr></thead>
                <tbody>
                  {countries.map(([c, r]) => (
                    <tr key={c} className="tabular-nums"><td className="font-medium">{c}</td><td>{pct(r.floor, 0)}</td><td>{pct(r.cap, 0)}</td><td>{pct(r.min_share, 0)}</td></tr>
                  ))}
                </tbody>
              </table>
              <div className="text-[11px] text-muted-foreground">discount = min(shipping down to cap × cart, margin above floor × cart)</div>
            </>
          )}
        </div>
      </div>

      {scopes.length > 0 && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-1 border-b border-border">
            {scopes.map((s) => (
              <button key={s} type="button" onClick={() => setScope(s)}
                className={`px-3 py-1.5 text-sm -mb-px border-b-2 ${s === activeScope ? 'border-primary text-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                {s === 'ALL' ? 'All countries' : s}
              </button>
            ))}
          </div>
          {activeScope && <ScopePanel block={summary.per_scope[activeScope]} settlingDays={summary.settling_days} franco={franco} />}
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <CumulativeChart series={summary.series} field="conversion" title="Conversion of eligible carts, cumulative (all countries)" format={(v) => pct(v, 0)} />
        <CumulativeChart series={summary.series} field="profit_per_cart" title="Profit per eligible cart, cumulative (all countries)" format={(v) => eur(v, 0)} />
      </div>

      <div className="grid gap-3 lg:grid-cols-[1fr_2fr]">
        <div className="rounded-lg border border-border p-4">
          <div className="text-sm font-medium mb-2">Quotes by outcome</div>
          <div className="space-y-1.5">
            {summary.funnel.by_reason.map((r) => (
              <div key={r.reason} className="grid grid-cols-[1fr_auto] gap-2 text-xs">
                <div>
                  <div className="flex justify-between"><span>{r.label}</span></div>
                  <Bar value={r.quotes} max={summary.funnel.quotes} label={r.label} className={r.reason === 'eligible' || r.reason === 'budget' ? 'bg-primary' : 'bg-muted-foreground/40'} />
                </div>
                <span className="tabular-nums text-muted-foreground self-end">{r.quotes} · {pct(r.quotes / (summary.funnel.quotes || 1), 0)}</span>
              </div>
            ))}
            {summary.funnel.by_reason.length === 0 && <div className="text-xs text-muted-foreground">No quote logged yet.</div>}
          </div>
        </div>
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="px-4 pt-3 pb-2 text-sm font-medium">Recent quotes</div>
          <div className="overflow-x-auto max-h-[420px]">
            <table className="w-full text-xs">
              <thead className="bg-muted/50 text-left sticky top-0">
                <tr>
                  <th className="px-3 py-1.5 font-semibold">When</th>
                  <th className="px-3 py-1.5 font-semibold">Arm</th>
                  <th className="px-3 py-1.5 font-semibold">Country</th>
                  <th className="px-3 py-1.5 font-semibold text-right">Cart</th>
                  <th className="px-3 py-1.5 font-semibold text-right">Shipping before</th>
                  <th className="px-3 py-1.5 font-semibold text-right">After</th>
                  <th className="px-3 py-1.5 font-semibold">Outcome</th>
                  <th className="px-3 py-1.5 font-semibold">Order</th>
                </tr>
              </thead>
              <tbody>
                {summary.recent.map((r) => (
                  <tr key={r.id} className="border-t border-border/60">
                    <td className="px-3 py-1 whitespace-nowrap">{dts(r.created_at)}{r.quotes > 1 ? <span className="text-muted-foreground"> ×{r.quotes}</span> : ''}</td>
                    <td className="px-3 py-1"><span className="inline-flex items-center gap-1.5"><ArmDot arm={r.arm} /> {r.arm}</span></td>
                    <td className="px-3 py-1">{r.country}</td>
                    <td className="px-3 py-1 text-right tabular-nums">{eur(r.cart_value)}</td>
                    <td className="px-3 py-1 text-right tabular-nums">{eur(r.shipping_before)}</td>
                    <td className="px-3 py-1 text-right tabular-nums">{r.applied ? <b>{eur(r.shipping_after)}</b> : eur(r.shipping_after)}</td>
                    <td className="px-3 py-1">
                      {r.applied ? (franco ? `free shipping (${eur(r.discount)})` : `discount ${eur(r.discount)}`) : (r.reason === 'eligible' ? `shadow ${eur(r.discount)}` : r.reason_label)}
                    </td>
                    <td className="px-3 py-1">{r.converted ? <span className="text-emerald-700 dark:text-emerald-300 font-medium">yes</span> : <span className="text-muted-foreground">no</span>}</td>
                  </tr>
                ))}
                {summary.recent.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-4 text-center text-muted-foreground">No quote logged yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {exp.notes && <div className="text-[11px] text-muted-foreground whitespace-pre-line">{exp.notes}</div>}
    </div>
  );
}


const VERDICT_STYLE = {
  too_early: 'bg-muted text-muted-foreground',
  unclear: 'bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200',
  ahead: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-200',
  behind: 'bg-red-100 text-red-900 dark:bg-red-950/50 dark:text-red-200',
};


// The country's usual order, for context: experiment carts are smaller by
// design (a shipping discount only exists where shipping is a big share).
function NormalAov({ n, className = '' }) {
  if (!n) return null;
  return (
    <div className={`text-[11px] text-muted-foreground ${className}`}>
      Usual order in {n.label || 'these countries'}: <span className="text-foreground font-medium tabular-nums">{eur(n.mean, 0)}</span> average,{' '}
      <span className="text-foreground font-medium tabular-nums">{eur(n.median, 0)}</span> median ({n.orders.toLocaleString()} orders, last {n.days} days, products ex VAT).
      Compare with the average cart and AOV above to see which orders fall in the test.
    </div>
  );
}

const CALL_STYLE = {
  too_early: 'bg-muted text-muted-foreground',
  pause: 'bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200',
  go: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-200',
  stop: 'bg-red-100 text-red-900 dark:bg-red-950/50 dark:text-red-200',
};

function PooledDecisionCard({ p }) {
  if (!p) return null;
  const c = p.arms?.control || {};
  const t = p.arms?.treatment || {};
  const conv = p.compare?.conversion;
  const prof = p.compare?.profit;
  const r = p.rule || {};
  const franco = p.theme === 'franco_threshold';
  const decided = !!p.decision_date;
  const row = (label, a, dot) => (
    <tr className="border-t border-border/50">
      <td className="py-1 pr-2"><span className={`inline-block h-2 w-2 rounded-full mr-1.5 ${dot}`} />{label}</td>
      <td className="py-1 px-2 text-right tabular-nums">{a.carts ?? 0}{a.settling_carts ? <span className="text-muted-foreground"> +{a.settling_carts}</span> : null}</td>
      <td className="py-1 px-2 text-right tabular-nums">{a.orders ?? 0}{a.settling_orders ? <span className="text-muted-foreground"> +{a.settling_orders}</span> : null}</td>
      <td className="py-1 px-2 text-right tabular-nums">{pct(a.conversion, 1)}</td>
      <td className="py-1 pl-2 text-right tabular-nums">{eur(a.profit_per_cart)}</td>
      <td className="py-1 pl-2 text-right tabular-nums">{eur(a.avg_cart_value, 0)}</td>
      <td className="py-1 pl-2 text-right tabular-nums">{eur(a.basket_per_order)}</td>
      <td className="py-1 pl-2 text-right tabular-nums font-semibold">{eur(a.profit_total, 0)}</td>
      {franco && <td className="py-1 pl-2 text-right tabular-nums">{eur(a.discount_spent, 0)}</td>}
    </tr>
  );
  return (
    <div className="rounded-lg border-2 border-foreground/15 bg-card p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{decided ? 'Pooled decision' : 'Pooled read'}</div>
          <div className="text-lg font-semibold leading-tight">{p.name}</div>
          <div className="text-xs text-muted-foreground">Week {p.week}{decided ? <> · decision on {dstr(p.decision_date)}, before Black Friday</> : <> · {p.experiments} tests read together, no decision date set</>}</div>
        </div>
        {decided && <div className="text-right">
          <div className="text-3xl font-semibold tabular-nums">{p.days_left ?? '–'}</div>
          <div className="text-xs text-muted-foreground">days to decide</div>
        </div>}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <table className="w-full text-xs">
          <thead className="text-muted-foreground">
            <tr><th className="text-left font-normal pb-1">Group, all {p.experiments || 3} tests</th><th className="text-right font-normal px-2" title="Settled eligible carts, + still settling">Eligible carts</th><th className="text-right font-normal px-2" title="Orders from settled carts, + orders from carts still settling">Orders</th><th className="text-right font-normal px-2">Conversion</th><th className="text-right font-normal pl-2">Profit / cart</th><th className="text-right font-normal pl-2" title="Average products value of the carts that entered the test, ex VAT">Avg cart</th><th className="text-right font-normal pl-2" title="Average products value of the orders placed (after discounts, ex VAT, shipping not included)">AOV</th><th className="text-right font-normal pl-2" title="Sum of the profit of every order in the group, after the discount or free shipping given">Total profit</th>{franco && <th className="text-right font-normal pl-2" title="Free shipping given on attributed orders (what they would have paid, ex VAT)">Giveaway</th>}</tr>
          </thead>
          <tbody>
            {row('Control', c, 'bg-blue-500')}
            {row('Test group', t, 'bg-orange-500')}
          </tbody>
        </table>
        <SettlingNote arms={{ c, t }} settlingDays={p.settling_days} />
        <NormalAov n={p.normal_aov} />
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="rounded border border-border p-2"><div className="text-muted-foreground">P(test converts better)</div><div className="text-base font-semibold tabular-nums">{conv ? pct(conv.p_better, 0) : '–'}</div><div className="text-muted-foreground tabular-nums">{conv ? `${conv.diff >= 0 ? '+' : ''}${(conv.diff * 100).toFixed(1)} pts` : 'needs carts in both groups'}</div></div>
          <div className="rounded border border-border p-2"><div className="text-muted-foreground">P(test earns more per cart)</div><div className="text-base font-semibold tabular-nums">{prof ? pct(prof.p_better, 0) : '–'}</div><div className="text-muted-foreground tabular-nums">{prof ? `${prof.diff >= 0 ? '+' : ''}${eur(prof.diff)} per cart` : 'needs orders in both groups'}</div></div>
          <div className="col-span-2 rounded border border-border p-2"><div className="text-muted-foreground">Net result vs normal prices, after everything given away</div><div className={`text-base font-semibold tabular-nums ${(p.compare?.extra_profit ?? 0) >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>{p.compare?.extra_profit == null ? '–' : `${p.compare.extra_profit >= 0 ? '+' : '-'}${eur(Math.abs(p.compare.extra_profit), 0)}`}</div><div className="text-muted-foreground">Test carts x (test profit per cart - normal-price profit per cart)</div></div>
        </div>
      </div>
      <div className="text-[11px] text-muted-foreground">
        Per test (control / test eligible carts, incl. settling): {(p.per_experiment || []).map((e) => `${e.name.replace(' shipping discount', '').replace(' (learning test)', '').replace(' (plain, no margin floor)', '')} ${e.control.carts + e.control.settling} / ${e.treatment.carts + e.treatment.settling}`).join(' · ')}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={`rounded px-2 py-0.5 font-medium ${CALL_STYLE[p.call] || CALL_STYLE.too_early}`}>{p.call_text}</span>
      </div>
      {!decided && <div className="text-[11px] text-muted-foreground leading-relaxed">
        The three franco tests use different thresholds but ask the same question, so their carts are read together: conversion and profit per eligible cart, and basket per order (does free shipping from a lower threshold shrink the basket?). It needs 100 settled carts per group before it means anything; each test also has its own card below.
      </div>}
      {decided && <div className="text-[11px] text-muted-foreground leading-relaxed">
        <strong className="text-foreground">Rule on {dstr(p.decision_date)}:</strong>{' '}
        GO = P(converts better) ≥ {pct(r.go_p_conversion, 0)} and profit per eligible cart not lower than control: roll the discount out to all addresses for Black Friday and December.{' '}
        STOP = P(converts better) ≤ {pct(r.stop_p_conversion, 0)}, or from week 4 P(earns more per cart) under {pct(r.stop_p_profit, 0)}: end the tests.{' '}
        PAUSE = anything else: off over Black Friday and December, resume in January.{' '}
        The call shown now is where the numbers point today, not the decision; it needs 100 settled carts per group before it means anything.
      </div>}
    </div>
  );
}

function ExperimentCard({ e, onOpen }) {
  const o = e.overview;
  const franco = e.rule_type === 'franco_threshold';
  const rule = Object.entries(e.countries || {}).map(([c, r]) => (franco ? `${c} €${r.from}→€${r.to}` : `${c} ${pct(r.floor, 0)}`)).join(' · ');
  const armRow = (label, a, dot) => (
    <tr className="border-t border-border/50">
      <td className="py-1 pr-2"><span className={`inline-block h-2 w-2 rounded-full mr-1.5 ${dot}`} />{label}</td>
      <td className="py-1 px-2 text-right tabular-nums">{a?.quotes ?? 0}</td>
      <td className="py-1 px-2 text-right tabular-nums">{a?.carts ?? 0}{a?.settling_carts ? <span className="text-muted-foreground"> +{a.settling_carts}</span> : null}</td>
      <td className="py-1 px-2 text-right tabular-nums">{a?.orders ?? 0}{a?.settling_orders ? <span className="text-muted-foreground"> +{a.settling_orders}</span> : null}</td>
      <td className="py-1 px-2 text-right tabular-nums">{pct(a?.conversion, 0)}</td>
      <td className="py-1 pl-2 text-right tabular-nums">{eur(a?.profit_per_cart)}</td>
      <td className="py-1 pl-2 text-right tabular-nums">{eur(a?.avg_cart_value, 0)}</td>
      <td className="py-1 pl-2 text-right tabular-nums">{eur(a?.basket_per_order, 0)}</td>
      <td className="py-1 pl-2 text-right tabular-nums font-semibold">{eur(a?.profit_total, 0)}</td>
    </tr>
  );
  const b = o?.budget;
  const spentPct = b && b.budget ? Math.min(100, (100 * (b.spent || 0)) / b.budget) : 0;
  return (
    <button type="button" onClick={onOpen}
      className="text-left rounded-lg border border-border bg-card p-4 space-y-3 hover:border-foreground/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium leading-tight">{e.name}</div>
          <div className="text-[11px] text-muted-foreground">{franco ? 'Free-shipping threshold' : 'Shipping discount, margin floor'} · {rule}</div>
        </div>
        <StatusBadge status={e.status} />
      </div>
      <OverlapChips overlaps={e.overlaps} className="flex" />
      {!o ? (
        <div className="text-xs text-muted-foreground">{e.status === 'draft' ? 'Draft: not started.' : 'No live numbers available.'}</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div><div className="text-muted-foreground">Progress</div><div className="font-semibold tabular-nums">Day {o.day_x} / {o.planned_days}</div>
              <div className="mt-1 h-1.5 rounded bg-muted"><div className="h-1.5 rounded bg-foreground/60" style={{ width: `${Math.min(100, (100 * o.day_x) / (o.planned_days || 1))}%` }} /></div></div>
            <div><div className="text-muted-foreground">Next checkpoint</div><div className="font-semibold tabular-nums">{o.next_checkpoint_week ? `Week ${o.next_checkpoint_week}` : 'Ended'}</div>
              <div className="text-[11px] text-muted-foreground">{o.next_checkpoint_date ? dstr(o.next_checkpoint_date) : ''}</div></div>
            <div><div className="text-muted-foreground">{franco ? 'Free shipping given' : 'Discount given'} (this week)</div><div className="font-semibold tabular-nums">{eur(b?.spent, 0)} <span className="font-normal text-muted-foreground">of {eur(b?.budget, 0)}</span></div>
              <div className="mt-1 h-1.5 rounded bg-muted"><div className={`h-1.5 rounded ${b?.exhausted ? 'bg-red-500' : 'bg-foreground/60'}`} style={{ width: `${spentPct}%` }} /></div></div>
          </div>
          <div className="text-xs text-muted-foreground">
            <span className="tabular-nums text-foreground font-medium">{o.quotes ?? 0}</span> quotes logged → <span className="tabular-nums text-foreground font-medium">{o.eligible_quotes ?? 0}</span> eligible
            {o.not_eligible_top?.length > 0 && <> · not eligible: {o.not_eligible_top.map((r) => `${r.label} ${r.quotes}`).join(', ')}</>}
            {o.orders_total > 0 && <> · <span className="tabular-nums text-foreground font-medium">{o.orders_total}</span> order{o.orders_total === 1 ? '' : 's'} attributed (only those from eligible carts count below)</>}
          </div>
          <table className="w-full text-xs">
            <thead className="text-muted-foreground">
              <tr><th className="text-left font-normal pb-1">Group</th><th className="text-right font-normal px-2">Quotes</th><th className="text-right font-normal px-2" title="Settled eligible carts, + still settling (under 3 days old)">Eligible carts</th><th className="text-right font-normal px-2" title="Orders from settled carts, + orders from carts still settling (counted in Discount given, not yet in the rates)">Orders</th><th className="text-right font-normal px-2">Conversion</th><th className="text-right font-normal pl-2">Profit / cart</th><th className="text-right font-normal pl-2" title="Average products value of the carts that entered the test, ex VAT">Avg cart</th><th className="text-right font-normal pl-2" title="Average products value of the orders placed, ex VAT">AOV</th><th className="text-right font-normal pl-2" title="Sum of the profit of every order, after the discount or free shipping given">Total profit</th></tr>
            </thead>
            <tbody>
              {armRow('Control', o.control, 'bg-blue-500')}
              {armRow('Test group', o.treatment, 'bg-orange-500')}
            </tbody>
          </table>
          <NormalAov n={o.normal_aov} />
          <SettlingNote arms={{ c: o.control, t: o.treatment }} settlingDays={o.settling_days} discountLabel={franco ? 'Free shipping given' : 'Discount given'} />
          {Object.keys(o.by_country || {}).length > 1 && (
            <div className="text-[11px] text-muted-foreground">Per country (control / test eligible carts): {Object.entries(o.by_country).map(([c, v]) => `${c} ${v.control.carts + v.control.settling} / ${v.treatment.carts + v.treatment.settling}`).join(' · ')}</div>
          )}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className={`rounded px-2 py-0.5 font-medium ${VERDICT_STYLE[o.verdict] || VERDICT_STYLE.too_early}`}>{o.verdict_text}</span>
            {o.p_conversion_better !== null && o.p_conversion_better !== undefined && <span className="text-muted-foreground">P(test converts better) {pct(o.p_conversion_better, 0)}</span>}
            {o.p_profit_better !== null && o.p_profit_better !== undefined && <span className="text-muted-foreground">P(test earns more per cart) {pct(o.p_profit_better, 0)}</span>}
          </div>
        </>
      )}
    </button>
  );
}

export default function Experiments() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axiosInstance.get('api/experiments/');
      setList(r.data);
      setError('');
    } catch (e) {
      setError(`Could not load experiments (${e?.response?.status || e.message}).`);
    }
  }, []);
  useEffect(() => { if (!id) load(); }, [id, load]);
  useEffect(() => {
    if (id) return undefined;
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 60000);
    return () => clearInterval(t);
  }, [id, load]);

  const experiments = useMemo(() => list?.experiments || [], [list]);

  return (
    <div className="p-3 md:p-6 space-y-5 max-w-[1400px]">
      {id ? (
        <Detail id={id} onBack={() => navigate('/experiments')} />
      ) : (
        <>
          <div>
            <h1 className="text-xl font-semibold flex items-center gap-2"><FlaskConical className="h-5 w-5" /> Shipping Experiments</h1>
            <p className="text-sm text-muted-foreground max-w-3xl">
              A/B tests at checkout. Shipping discount: half of the addresses in a country see a lower
              default rate on carts with enough margin; the other half keeps the normal price and logs the same discount as
              a shadow. Franco threshold: half of the addresses get free shipping from a lower threshold (the normal Free Shipping
              rate set), the other half keeps today&rsquo;s threshold. Rule and safeguards: Checkout Guide, &ldquo;Shipping Experiments&rdquo;.
            </p>
          </div>
          {error && <div className="rounded border border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200 px-3 py-2 text-sm">{error}</div>}
          {!list && !error && <ShippyLoader message="Reading the experiments" />}
          {list && !list.kill_switch_on && (
            <div className="rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm flex items-center gap-2"><Info className="h-4 w-4 shrink-0" />
              Global switch <code className="text-xs">PackingConfig.SHIPPING_EXPERIMENTS_ENABLED</code> is off: no experiment touches checkout.
            </div>
          )}
          {list && (list.themes || []).length > 0 && (list.themes || []).map((t) => {
            const mine = experiments.filter((e) => (t.experiment_ids || []).includes(e.id));
            return (
              <section key={t.key} className="space-y-3">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border pb-1">
                  <h2 className="text-base font-semibold">{t.title}</h2>
                  <span className="text-xs text-muted-foreground">{t.running} running · {mine.length} in total</span>
                  <span className="text-xs text-muted-foreground italic">{t.question}</span>
                </div>
                {t.pooled && <PooledDecisionCard p={t.pooled} />}
                <div className="grid gap-4 md:grid-cols-2">
                  {mine.map((e) => <ExperimentCard key={e.id} e={e} onOpen={() => navigate(`/experiments/${e.id}`)} />)}
                </div>
              </section>
            );
          })}
          {list && (list.themes || []).length === 0 && (
            <>
              {list.pooled && <PooledDecisionCard p={list.pooled} />}
              <div className="grid gap-4 md:grid-cols-2">
                {experiments.map((e) => <ExperimentCard key={e.id} e={e} onOpen={() => navigate(`/experiments/${e.id}`)} />)}
                {experiments.length === 0 && (
                  <div className="text-sm text-muted-foreground">No experiment yet. Create the first one with <code>manage.py seed_shipping_experiment</code>.</div>
                )}
              </div>
            </>
          )}
          {list && <div className="text-[11px] text-muted-foreground">Numbers pool all countries of an experiment. Only settled eligible carts (3 days after their first quote) count in conversion; &ldquo;+N&rdquo; are still settling. A verdict needs at least 30 settled carts in each group; read at the checkpoints, not day to day.</div>}
          {list && <div className="text-[11px] text-muted-foreground">Weekly summary: <code>manage.py experiment_weekly_report</code> to {list.slack_channel || 'not configured'} (SLACK_EXPERIMENT_CHANNEL, else #shippy).</div>}
        </>
      )}
    </div>
  );
}
